import { Component, Injector, OnDestroy } from '@angular/core';
import {
    GetInvoicesByRelationshipRequest,
    GetInvoicesByRelationshipResponse,
    GetInvoicesByRelationshipService,
    ImageViewableStatus,
    RelationshipInvoice
} from '@lsq-frontend/remote-services/enterprise/dashboard-services';
import {
    ClassNames,
    DateFormat,
    getDynamicRouteLink,
    LifecycleAware
} from '@lsq-frontend/common-ui';
import { DaysPipe, getRouterLinkUrlById, Icons, ResponsiveService } from '@lsq-frontend/common-ui';
import { ModalService } from '@lsq-frontend/common-ui';
import { LsqApiServiceListSource } from '@lsq-frontend/angular-shared/api-service';
import { I18n } from '@ngx-translate/i18n-polyfill';
import { map } from 'rxjs/operators';
import { AnalyticsValue } from '../../../../../constants/analytics.constants';
import { ACCRUED_COLLECTION_DESCRIPTION } from '../../../../../constants/copy.constants';
import { atlas, INVOICES_INVOICE } from '@lsq-frontend/fasttrack-shared/routing-atlas';
import { InvoiceHistoricalImagePreviewModal } from '../../../../invoices/invoices.models';
import { ByCustomerDateAsOfReportComponent } from '../../../../reports/base/by-customer-date-as-of-report.component';
import { ReportSavedDateFilterNames } from '../../../../services/saved-date-filter.service';
import { CustomerDetailUserStateTypes } from '../../customer-detail.user-state-types';

@LifecycleAware()
@Component({
    selector: 'aging-by-customer',
    template: `
        <div class="container-fluid page-container" *ngIf="dataSource">
            <div class="row">
                <div class="col">
                    <lsq-data-table-mobile
                        *ngIf="responsiveService.smBelow | async"
                        [dataSource]="dataSource"
                        [isPaginated]="true"
                    >
                        <ng-template #mobileCardHeader let-row="row">
                            <div class="mtc-header-content">
                                <div class="mtc-header-title">
                                    {{ row.invoiceNumber }}
                                    <ng-container *ngIf="invoiceImageIsViewable(row)">
                                        <lsq-button
                                            appearance="transparent"
                                            [ngStyle]="{ fontSize: '1rem', marginLeft: '1rem' }"
                                            (buttonClick)="handleViewInvoiceButtonClick(row)"
                                            i18n="Link to open modal showing the invoice image"
                                        >
                                            View invoice image
                                        </lsq-button>
                                    </ng-container>
                                </div>
                                <div class="mtc-header-sub-title">
                                    <span i18n="Mobile table header label">Invoice Balance:</span>
                                    {{ row.invoiceBalance | lsqCurrency }}
                                </div>
                            </div>
                        </ng-template>
                        <ng-template #mobileCardColumns let-row="row">
                            <div class="card-item">
                                <div class="card-item-label">
                                    {{ dateHeader }}
                                </div>
                                <div class="card-item-value">
                                    {{ row.invoiceDate | date: dateFormat.LsqShortYear }}
                                </div>
                            </div>
                            <div class="card-item">
                                <div class="card-item-label">
                                    {{ poNumberHeader }}
                                </div>
                                <div class="card-item-value">
                                    {{ row.poNumber }}
                                </div>
                            </div>
                            <div class="card-item">
                                <div class="card-item-label">
                                    {{ amountHeader }}
                                </div>
                                <div class="card-item-value">
                                    {{ row.invoiceAmount | lsqCurrency }}
                                </div>
                            </div>
                            <div class="card-item">
                                <div class="card-item-label">
                                    {{ ageHeader }}
                                </div>
                                <div class="card-item-value">
                                    {{ daysPipe.transform(row.age) }}
                                </div>
                            </div>
                            <div class="card-item">
                                <div class="card-item-label">
                                    {{ accruedCollectionHeader1 + ' ' + accruedCollectionHeader2 }}
                                    <lsq-icon-popover
                                        [text]="accruedCollectionDescription"
                                    ></lsq-icon-popover>
                                </div>
                                <div class="card-item-value">
                                    {{ row.accruedFees | lsqCurrency }}
                                </div>
                            </div>
                        </ng-template>
                    </lsq-data-table-mobile>

                    <lsq-data-table
                        *ngIf="!(responsiveService.smBelow | async)"
                        [dataSource]="dataSource"
                        [isPaginated]="true"
                        [hasFooter]="true"
                        [hasStickyHeader]="true"
                        [columnVisibilities]="{
                            invoiceImage: showInvoiceImageColumn$ | async
                        }"
                        (takeChanged)="handleTakeChange($event)"
                    >
                        <lsq-row-count-column></lsq-row-count-column>

                        <invoice-preview-button-column></invoice-preview-button-column>

                        <lsq-column
                            [column]="{
                                header: 'Invoice #',
                                key: 'invoiceNumber',
                                truncateTextCss: true
                            }"
                        >
                            <ng-template [custom-cell] let-row="row">
                                <a
                                    [routerLink]="[
                                        getInvoiceLink(invoiceDetailRouterLinkUrl, row.invoiceKey)
                                    ]"
                                    class="preserve-case"
                                >
                                    {{ row.invoiceNumber }}
                                </a>
                            </ng-template>
                            <ng-template [custom-footer-cell] let-row="row">
                                <div class="grand-total">
                                    <span>Grand Total</span>
                                </div>
                            </ng-template>
                        </lsq-column>

                        <lsq-date-column
                            [column]="{
                                header: dateHeader,
                                key: 'invoiceDate',
                                className: 'invoice-date-col'
                            }"
                        >
                        </lsq-date-column>

                        <lsq-column
                            [column]="{
                                header: poNumberHeader,
                                key: 'poNumber',
                                width: '10%'
                            }"
                        >
                        </lsq-column>

                        <lsq-currency-column
                            [column]="{
                                header: amountHeader,
                                key: 'invoiceAmount',
                                className: 'invoice-amount-col'
                            }"
                            [totalValue]="(dataSource.data$ | async)?.totalAmount"
                        >
                        </lsq-currency-column>

                        <lsq-currency-column
                            [column]="{
                                header: invoiceBalanceHeader1 + '<br />' + invoiceBalanceHeader2,
                                key: 'invoiceBalance',
                                className: 'invoice-amount-col'
                            }"
                            [totalValue]="(dataSource.data$ | async)?.totalBalance"
                        >
                        </lsq-currency-column>

                        <lsq-spacer-column width="10%"></lsq-spacer-column>

                        <lsq-date-column
                            [isDays]="true"
                            [column]="{
                                header: ageHeader,
                                key: 'age',
                                className: 'age-col'
                            }"
                        >
                        </lsq-date-column>

                        <lsq-currency-column
                            [column]="{
                                header:
                                    accruedCollectionHeader1 + '<br />' + accruedCollectionHeader2,
                                key: 'accruedFees',
                                headerPopoverContent: accruedCollectionDescription,
                                className: 'invoice-amount-col'
                            }"
                            [totalValue]="(dataSource.data$ | async)?.totalFees"
                        >
                        </lsq-currency-column>
                    </lsq-data-table>
                </div>
            </div>
        </div>
    `
})
export class AgingByCustomerReportComponent extends ByCustomerDateAsOfReportComponent
    implements OnDestroy {
    public title = this._i18n({
        value: 'Aging',
        meaning: 'Page title'
    });
    public analyticsName = AnalyticsValue.AgingCustomer;
    public userStateType = CustomerDetailUserStateTypes.GetInvoicesByRelationshipRequest;
    public savedDateFilterName = ReportSavedDateFilterNames.AgingByCustomerReport;

    public dataSource = new LsqApiServiceListSource<
        GetInvoicesByRelationshipResponse,
        GetInvoicesByRelationshipRequest,
        RelationshipInvoice
    >(this.apiService);

    public dateHeader = this._i18n({
        value: 'Invoice Date',
        meaning: 'Table column header'
    });
    public poNumberHeader = this._i18n({
        value: 'P.O. #',
        meaning: 'Table column header'
    });
    public amountHeader = this._i18n({
        value: 'Amount',
        meaning: 'Table column header'
    });
    public invoiceBalanceHeader1 = this._i18n({
        value: 'Invoice',
        meaning: 'Table column header - Part 1/2 of: Invoice Balance'
    });
    public invoiceBalanceHeader2 = this._i18n({
        value: 'Balance',
        meaning: 'Table column header - Part 2/2 of: Invoice Balance'
    });
    public ageHeader = this._i18n({
        value: 'Age',
        meaning: 'Table column header'
    });
    public accruedCollectionHeader1 = this._i18n({
        value: 'Accrued',
        meaning: 'Table column header - Part 1/2 of: Accrued Collections Fees'
    });
    public accruedCollectionHeader2 = this._i18n({
        value: 'Collections Fees',
        meaning: 'Table column header - Part 2/2 of: Accrued Collections Fees'
    });

    // Constants
    public accruedCollectionDescription = ACCRUED_COLLECTION_DESCRIPTION;
    public classNames = ClassNames;
    public dateFormat = DateFormat;
    public icons = Icons;
    public invoiceDetailRouterLinkUrl = getRouterLinkUrlById(atlas, INVOICES_INVOICE);
    public showInvoiceImageColumn$ = this.dataSource.data$.pipe(
        map((response: GetInvoicesByRelationshipResponse) =>
            response.rows.some(
                ({ imageViewableStatus }) => imageViewableStatus === ImageViewableStatus.Viewable
            )
        )
    );

    constructor(
        private _i18n: I18n,
        public apiService: GetInvoicesByRelationshipService,
        public daysPipe: DaysPipe,
        public responsiveService: ResponsiveService,
        public injector: Injector
    ) {
        super(injector);

        this.dispatchInitialRequest();
    }

    public ngOnDestroy(): void {}

    public getInvoiceLink(link: string, invoiceKey: string): string {
        return getDynamicRouteLink(link, { invoiceKey });
    }

    public handleViewInvoiceButtonClick(invoice: RelationshipInvoice): void {
        if (invoice.imageViewableStatus === ImageViewableStatus.Viewable) {
            const modalService = this.injector.get(ModalService);
            modalService.showModal(new InvoiceHistoricalImagePreviewModal(invoice));
        }
    }

    public invoiceImageIsViewable({ imageViewableStatus }: RelationshipInvoice): boolean {
        return imageViewableStatus === ImageViewableStatus.Viewable;
    }
}
